import React, { useState, useEffect } from 'react';
import ChartPanel from './ChartPanel'; // Import ChartPanel for the chart display
import { Link } from 'react-router-dom';

export default function TradePanel() {
  const [trades, setTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(647.61);
  const [timerDuration, setTimerDuration] = useState(60);
  const [tradeAmountUSD, setTradeAmountUSD] = useState('');
  const [totalProfit, setTotalProfit] = useState(0);
  const [totalLoss, setTotalLoss] = useState(0);
  const [walletBalance, setWalletBalance] = useState(1000); // Initial wallet balance

  useEffect(() => {
    const socket = new WebSocket('wss://stream.binance.com:9443/ws/bnbusdt@trade');
    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      setCurrentPrice(parseFloat(data.p)); // Update current price from WebSocket
    };

    return () => {
      socket.close();
    };
  }, []);

  const calculateAmountOfBNB = (usdAmount) => {
    return usdAmount / currentPrice;
  };

  const handleTrade = (type) => {
    if (!tradeAmountUSD) {
      alert("Please fill field: Trade amount.");
      return;
    }
    if (parseFloat(tradeAmountUSD) > walletBalance) {
      alert("Insufficient balance to make this trade!");
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const endTime = now + timerDuration;
    const price = currentPrice;

    const amountBNB = calculateAmountOfBNB(parseFloat(tradeAmountUSD));

    const newTrade = {
      type,
      price,
      amountBNB,
      time: now,
      endTime,
      remaining: timerDuration,
      profitLoss: 0,
      isClosed: false,
      tradeAmountUSD: parseFloat(tradeAmountUSD),
    };

    const updatedTrades = [...trades, newTrade];
    updatedTrades.sort((a, b) => a.time - b.time);

    setTrades(updatedTrades);
    setWalletBalance(walletBalance - parseFloat(tradeAmountUSD)); // Update wallet balance after trade
    setTradeAmountUSD('');
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);

      setTrades((prevTrades) =>
        prevTrades
          .map((t) => {
            const remainingTime = Math.max(0, t.endTime - now);
            let updatedTrade = { ...t, remaining: remainingTime };

            // Real-time profit/loss calculation for open trades
            if (!updatedTrade.isClosed) {
              updatedTrade.profitLoss = (currentPrice - updatedTrade.price) * updatedTrade.amountBNB;

              if (updatedTrade.profitLoss > 0) {
                setTotalProfit((prev) => prev + updatedTrade.profitLoss);
              } else {
                setTotalLoss((prev) => prev + Math.abs(updatedTrade.profitLoss));
              }
            }

            // Close trade when timer runs out
            if (remainingTime === 0 && !t.isClosed) {
              updatedTrade.isClosed = true;
              updatedTrade.profitLoss = (currentPrice - updatedTrade.price) * updatedTrade.amountBNB;

              if (updatedTrade.profitLoss > 0) {
                setTotalProfit((prev) => prev + updatedTrade.profitLoss);
              } else {
                setTotalLoss((prev) => prev + Math.abs(updatedTrade.profitLoss));
              }

              setWalletBalance((prev) => prev + updatedTrade.profitLoss); // Update wallet balance after trade closure

              const updatedClosedTrades = [...closedTrades, { ...updatedTrade, closeTime: now, closePrice: currentPrice }];
              updatedClosedTrades.sort((a, b) => a.time - b.time);
              setClosedTrades(updatedClosedTrades);
            }

            return updatedTrade;
          })
          .filter((t) => t.endTime > now || t.isClosed) // Keep open or closed trades in the list
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [trades, currentPrice]);

  return (
    <div
      style={{
        backgroundColor: '#000',
        color: '#fff',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
      }}
    >
      <h2 style={{ textAlign: 'center' }}>Time-Based Trading</h2>

      {/* Navigation button to switch to Points-Based Trading */}
      <div style={{ marginBottom: '20px' }}>
        <Link to="/points-based">
          <button
            style={{
              background: 'blue',
              color: '#fff',
              padding: '12px 24px',
              fontSize: '16px',
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Go to Points-Based Trading
          </button>
        </Link>
      </div>

      <ChartPanel trades={trades} setCurrentPrice={setCurrentPrice} /> {/* Include ChartPanel here */}

      {/* Timer Input */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ color: '#fff' }}>Select Timer (in seconds): </label>
        <input
          type="number"
          value={timerDuration}
          onChange={(e) => setTimerDuration(Number(e.target.value))}
          style={{
            padding: '5px 10px',
            margin: '0 10px',
            borderRadius: '4px',
            border: '1px solid #fff',
            color: '#000',
          }}
        />
        <span style={{ color: '#fff' }}>seconds</span>
      </div>

      {/* Trade Amount Input */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ color: '#fff' }}>Enter USD Amount for Trade: </label>
        <input
          type="number"
          value={tradeAmountUSD}
          onChange={(e) => setTradeAmountUSD(e.target.value)}
          placeholder="Enter amount"
          style={{
            padding: '5px 10px',
            margin: '0 10px',
            borderRadius: '4px',
            border: '1px solid #fff',
            color: '#000',
          }}
        />
        <span style={{ color: '#fff' }}>USD</span>
      </div>

      {/* CALL/PUT buttons */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
        <button
          onClick={() => handleTrade('CALL')}
          style={{
            background: 'green',
            color: '#fff',
            padding: '12px 24px',
            fontSize: '16px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          CALL ↑
        </button>
        <button
          onClick={() => handleTrade('PUT')}
          style={{
            background: 'red',
            color: '#fff',
            padding: '12px 24px',
            fontSize: '16px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
          }}
        >
          PUT ↓
        </button>
      </div>

      {/* Open Positions Section */}
      <div style={{ width: '100%', maxWidth: '900px', background: '#111', padding: 15, borderRadius: 6 }}>
        <h3 style={{ marginBottom: 10 }}>Open Positions</h3>
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {trades.map((trade, i) => (
            <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid #333' }}>
              {new Date(trade.time * 1000).toLocaleTimeString()} —
              <b>{trade.type}</b> @ ${trade.price} —
              Amount: ${trade.tradeAmountUSD} —
              ⏳ {trade.remaining > 0 ? `${trade.remaining}s` : 'Closed'}
              <div style={{ color: trade.profitLoss > 0 ? 'green' : 'red', marginTop: '5px' }}>
                Profit/Loss: ${trade.profitLoss !== null ? trade.profitLoss.toFixed(2) : 'N/A'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Closed Positions Section */}
      <div style={{ width: '100%', maxWidth: '900px', background: '#111', padding: 15, borderRadius: 6 }}>
        <h3 style={{ marginBottom: 10 }}>Closed Positions</h3>
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {closedTrades.map((trade, i) => (
            <div key={i} style={{ padding: '6px 0', borderBottom: '1px solid #333' }}>
              {new Date(trade.time * 1000).toLocaleTimeString()} —
              <b>{trade.type}</b> @ ${trade.price} —
              Amount: ${trade.tradeAmountUSD} —
              Closed at {new Date(trade.closeTime * 1000).toLocaleTimeString()} @ ${trade.closePrice} —
              Profit/Loss: ${trade.profitLoss.toFixed(2)}
            </div>
          ))}
        </div>
      </div>

      {/* Total Profit, Loss, and Wallet Balance */}
      <div style={{
        marginTop: '20px',
        color: '#fff',
        fontSize: '16px',
        padding: '10px 0',
        backgroundColor: '#333',
        borderRadius: '6px',
        width: '100%',
        maxWidth: '900px',
        textAlign: 'center'
      }}>
        <h3>Total Profit: ${totalProfit.toFixed(2)}</h3>
      </div>

      <div style={{
        marginTop: '20px',
        color: '#fff',
        fontSize: '16px',
        padding: '10px 0',
        backgroundColor: '#333',
        borderRadius: '6px',
        width: '100%',
        maxWidth: '900px',
        textAlign: 'center'
      }}>
        <h3>Total Loss: ${totalLoss.toFixed(2)}</h3>
      </div>

      <div style={{
        marginTop: '20px',
        color: '#fff',
        fontSize: '16px',
        padding: '10px 0',
        backgroundColor: '#333',
        borderRadius: '6px',
        width: '100%',
        maxWidth: '900px',
        textAlign: 'center'
      }}>
        <h3>Wallet Balance: ${walletBalance.toFixed(2)}</h3>
      </div>
    </div>
  );
}
